import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Area, AreaChart, CartesianGrid
} from 'recharts';
import { 
  TrendingUp, Users, CheckCircle, DollarSign, Target, MessageCircle,
  AlertTriangle, ArrowLeft, RefreshCw, Calendar, Trophy, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/helpers';

const STAGE_LABELS = {
  lead: 'Lead',
  primeiro_contato: '1º Contato',
  proposta_enviada: 'Proposta',
  negociacao: 'Negociação',
  fechado: 'Fechado',
  perdido: 'Perdido',
};

const STAGE_COLORS = {
  lead: '#94a3b8',
  primeiro_contato: '#3b82f6',
  proposta_enviada: '#f59e0b',
  negociacao: '#8b5cf6',
  fechado: '#22c55e',
  perdido: '#ef4444',
};

const CHART_COLORS = ['#6366f1', '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b'];

const PERIOD_OPTIONS = [
  { value: 7,   label: '7 dias' },
  { value: 30,  label: '30 dias' },
  { value: 90,  label: '90 dias' },
  { value: 180, label: '6 meses' },
];

function MetricCard({ icon: Icon, label, value, sub, accent = 'text-slate-900', bg = 'bg-white' }) {
  return (
    <div className={`${bg} p-5 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4`}>
      <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5 text-slate-600" />
      </div>
      <div>
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
        <p className={`text-2xl font-bold ${accent} mt-0.5 leading-none`}>{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

function openWhatsApp(phone) {
  if (!phone) return;
  const n = phone.replace(/\D/g, '');
  window.open(`https://wa.me/${n.startsWith('55') ? n : '55' + n}`, '_blank');
}

export default function CRMPerformance() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/stats?days=${period}`);
      setStats(res.data);
    } catch (e) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const funilarr = stats
    ? Object.entries(stats.funil || {}).map(([key, val]) => ({
        name: STAGE_LABELS[key] || key,
        leads: val,
        color: STAGE_COLORS[key] || '#6366f1',
      }))
    : [];

  const origemArr = stats
    ? Object.entries(stats.origens || {}).map(([key, val]) => ({ name: key, value: val }))
    : [];

  const insightsArr = [];
  if (stats) {
    if (stats.parados?.length > 3) {
      insightsArr.push({ type: 'warn', msg: `${stats.parados.length} leads parados há mais de 7 dias. Hora de agir!` });
    }
    if (stats.taxa_conversao > 30) {
      insightsArr.push({ type: 'success', msg: `Taxa de conversão excelente: ${stats.taxa_conversao}%! Continue assim.` });
    } else if (stats.taxa_conversao < 10 && stats.total > 5) {
      insightsArr.push({ type: 'warn', msg: `Taxa de conversão baixa: ${stats.taxa_conversao}%. Revise as propostas.` });
    }
    if (stats.valor_pipeline > 0) {
      insightsArr.push({ type: 'info', msg: `Pipeline de ${formatCurrency(stats.valor_pipeline)} em oportunidades abertas.` });
    }
  }

  return (
    <div className="space-y-6" data-testid="crm-performance-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/crm')} className="gap-1.5">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">Performance de Leads</h1>
            <p className="text-slate-500 mt-0.5">Análise estratégica do pipeline de vendas</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-all font-medium ${
                period === opt.value
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-primary/30'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <Button variant="ghost" size="icon" onClick={loadStats} className="h-8 w-8">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <RefreshCw className="animate-spin h-8 w-8 text-primary mx-auto mb-3" />
            <p className="text-slate-500">Carregando métricas...</p>
          </div>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <MetricCard icon={Users} label="Total de Leads" value={stats?.total ?? 0} accent="text-slate-900" />
            <MetricCard icon={Calendar} label={`Novos (${period}d)`} value={stats?.period_leads ?? 0} accent="text-blue-600" />
            <MetricCard icon={CheckCircle} label="Convertidos" value={stats?.converted ?? 0} accent="text-green-600" />
            <MetricCard icon={Target} label="Conversão" value={`${stats?.taxa_conversao ?? 0}%`} accent="text-violet-600" />
            <MetricCard icon={DollarSign} label="Pipeline Aberto" value={formatCurrency(stats?.valor_pipeline ?? 0)} accent="text-amber-600" />
            <MetricCard icon={Trophy} label="Ticket Médio" value={formatCurrency(stats?.ticket_medio ?? 0)} accent="text-emerald-600" />
          </div>

          {/* Insights */}
          {insightsArr.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {insightsArr.map((ins, i) => (
                <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border text-sm ${
                  ins.type === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                  ins.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
                  'bg-blue-50 border-blue-200 text-blue-800'
                }`}>
                  {ins.type === 'warn' ? <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" /> :
                   ins.type === 'success' ? <Zap className="h-4 w-4 flex-shrink-0 mt-0.5" /> :
                   <TrendingUp className="h-4 w-4 flex-shrink-0 mt-0.5" />}
                  <p className="leading-relaxed">{ins.msg}</p>
                </div>
              ))}
            </div>
          )}

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {/* Entradas ao longo do tempo */}
            <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">Entradas de Leads ({period} dias)</h3>
              {stats?.entries_chart?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={stats.entries_chart}>
                    <defs>
                      <linearGradient id="leadsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: 12 }}
                      labelFormatter={d => `Data: ${d}`}
                    />
                    <Area type="monotone" dataKey="leads" stroke="#6366f1" strokeWidth={2} fill="url(#leadsGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
                  Sem dados no período selecionado
                </div>
              )}
            </div>

            {/* Distribuição por Origem */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">Leads por Origem</h3>
              {origemArr.length > 0 ? (
                <div className="flex flex-col items-center gap-3">
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={origemArr} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" strokeWidth={2} stroke="#fff">
                        {origemArr.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '10px', fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="w-full space-y-1">
                    {origemArr.map((item, i) => (
                      <div key={item.name} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className="text-slate-600 truncate max-w-[100px]">{item.name}</span>
                        </div>
                        <span className="font-medium text-slate-800">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
                  Sem dados de origem registrados
                </div>
              )}
            </div>
          </div>

          {/* Funil de Conversão */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Funil de Conversão por Etapa</h3>
            <div className="space-y-2">
              {funilarr.map((stage, idx) => {
                const prev = idx > 0 ? funilarr[idx - 1].leads : stage.leads;
                const pct = funilarr[0]?.leads > 0 ? (stage.leads / funilarr[0].leads * 100).toFixed(1) : 0;
                const drop = idx > 0 && prev > 0 ? ((stage.leads / prev) * 100).toFixed(0) : null;
                return (
                  <div key={stage.name} className="flex items-center gap-3">
                    <div className="w-28 text-xs text-slate-600 font-medium text-right flex-shrink-0">{stage.name}</div>
                    <div className="flex-1 relative h-8 bg-slate-50 rounded-lg overflow-hidden">
                      <div
                        className="h-full rounded-lg transition-all duration-700 flex items-center pl-3"
                        style={{
                          width: `${Math.max(pct, 2)}%`,
                          background: stage.color,
                          opacity: 0.85,
                        }}
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-white">
                        {stage.leads}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 w-24 text-right justify-end">
                      <span className="text-xs text-slate-400">{pct}%</span>
                      {drop && idx > 0 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                          parseInt(drop) > 50 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                        }`}>
                          ↓{100 - parseInt(drop)}% saíram
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Leads Parados */}
          {stats?.parados?.length > 0 && (
            <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-slate-800">Leads Parados (sem interação &gt;7 dias)</h3>
                <Badge className="bg-amber-100 text-amber-700 text-xs">{stats.parados.length}</Badge>
              </div>
              <div className="space-y-2">
                {stats.parados.map(lead => (
                  <div key={lead.id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{lead.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-400">{STAGE_LABELS[lead.status] || lead.status}</span>
                        {lead.contact_name && <span className="text-xs text-slate-400">· {lead.contact_name}</span>}
                        {lead.valor_estimado > 0 && (
                          <span className="text-xs font-medium text-violet-600">{formatCurrency(lead.valor_estimado)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                        {lead.days_stopped}d parado
                      </span>
                      {lead.contact_phone && (
                        <button
                          className="flex items-center gap-1 text-xs py-1 px-2 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
                          onClick={() => openWhatsApp(lead.contact_phone)}
                        >
                          <MessageCircle className="h-3 w-3" /> Retomar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
